import {Component, Input, OnInit} from '@angular/core';
import {animate, keyframes, style, transition, trigger} from '@angular/animations';

import {Subscription, timer} from 'rxjs';
import {TemperatureUnit} from '../../../shared';
import {Snapshot} from '../../../shared/models/snapshot';
import {AirComponentType} from '../../../afb-api/enums/air-component-type.enum';
import {GoogleAnalyticsService} from '../../../core';
import {timeInterval} from 'rxjs/internal/operators';

@Component({
  selector: 'awair-device-selection',
  templateUrl: './device-selection.component.html',
  styleUrls: ['./device-selection.component.scss'],
  animations: [
    trigger('fadeInOut', [
      transition('* => void', []),
      transition('* => *', [
        animate('500ms ease-out', keyframes([
          style({opacity: 0}),
          style({opacity: 1})
        ]))])
    ])]
})

export class DeviceSelectionComponent implements OnInit {

  @Input() temperatureUnit: TemperatureUnit;
  AirComponentType = AirComponentType;
  currentIndex: number = 0;
  hoveredDeviceKeys: AirComponentType[] = [];
  selectedDeviceKeys: AirComponentType[] = [];
  private oldSnapshotLength: number = 0;
  private multiDeviceAutoChange: Subscription;

  constructor(private googleAnalyticsService: GoogleAnalyticsService) {
  }

  private _selectedSnapshots: Snapshot[] = [];

  // Snapshots
  get selectedSnapshots(): Snapshot[] {
    if (this._selectedSnapshots != null) {
      return this._selectedSnapshots;
    }
  }

  @Input()
  set selectedSnapshots(value: Snapshot[]) {
    if (value != null) {
      this._selectedSnapshots = [...value];
      this.selectedDeviceKeys = this.getDeviceKeys(value[this.currentIndex]);

      if (value.length > 0 && value.length !== this.oldSnapshotLength) {
        this.currentIndex = value.length - 1;

        if (this.multiDeviceAutoChange) {
          this.multiDeviceAutoChange.unsubscribe();
        }

        this.multiDeviceAutoChange = this.getCarouselTimer();
      } else if (value.length === 0 && this.multiDeviceAutoChange) {
        this.multiDeviceAutoChange.unsubscribe();
      }

      // Update oldSnapshotLength to compare devices length for next input
      this.oldSnapshotLength = value.length;
    }
  }

  private _hoveredSnapshot: Snapshot;

  get hoveredSnapshot(): Snapshot {
    if (this._hoveredSnapshot) {
      return this._hoveredSnapshot;
    }
  }

  @Input()
  set hoveredSnapshot(value: Snapshot) {
    this._hoveredSnapshot = value ? {...value} : null;
    this.hoveredDeviceKeys = this.getDeviceKeys(value);
  }

  ngOnInit() {
  }

  selectPrev(): void {
    // Track left navigation event
    this.googleAnalyticsService.emitEvent('Snapshot - Nav', 'click', 'Left');

    // Reset Timer
    if (this.multiDeviceAutoChange) {
      this.multiDeviceAutoChange.unsubscribe();
    }
    this.multiDeviceAutoChange = this.getCarouselTimer();

    // Loop effect
    this.currentIndex = this.currentIndex > 0 ? this.currentIndex - 1 : this.selectedSnapshots.length - 1;
    this.selectedDeviceKeys = this.getDeviceKeys(this.selectedSnapshots[this.currentIndex]);
  }

  selectNext(moveAround: boolean = false): void {
    // Track right navigation event
    if (!moveAround) {
      this.googleAnalyticsService.emitEvent('Snapshot - Nav', 'click', 'Right');
    }

    // Reset Timer
    if (this.multiDeviceAutoChange) {
      this.multiDeviceAutoChange.unsubscribe();
    }
    this.multiDeviceAutoChange = this.getCarouselTimer();

    // Loop effect
    this.currentIndex = this.currentIndex < this.selectedSnapshots.length - 1 ? this.currentIndex + 1 : 0;
    this.selectedDeviceKeys = this.getDeviceKeys(this.selectedSnapshots[this.currentIndex]);
  }

  private getDeviceKeys(snapshot: Snapshot): AirComponentType[] {
    if (snapshot != null && snapshot.sensors != null) {
      return Object.keys(snapshot.sensors)
        .filter((key: string) => key !== 'pm10')
        // Should sort in order
        .sort((a: string, b: string) => {
          const mapValue = {temp: 0, humid: 1, co2: 2, voc: 3, dust: 4, pm25: 5, pm10: 6};
          return mapValue[a] - mapValue[b];
        })
        .map((key: string) => <AirComponentType>key);
    }

    return [];
  }

  private getCarouselTimer(): Subscription {
    return timer(5000, 5000).pipe(
      timeInterval()
    ).subscribe(() => {
      this.selectNext(true);
    });
  }
}
